import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounced } from '@/hooks/use-debounced'
import {
  type NeighbourhoodQuery,
  type RegisterCountry,
  type RegisterImportRequest,
  registersApi,
  type SearchKind,
} from '@/services/api/registers'

// --- Query keys ---

export const registersKeys = {
  all: ['registers'] as const,
  overview: () => ['registers', 'overview'] as const,
  search: (country: RegisterCountry, q: string, kind: SearchKind, limit: number, page: number) =>
    ['registers', 'search', country, q, kind, limit, page] as const,
  neighbourhood: (query: NeighbourhoodQuery) =>
    [
      'registers',
      'graph',
      query.country ?? '',
      query.id ?? '',
      query.ico ?? '',
      query.depth ?? 2,
      query.maxNodes ?? 120,
    ] as const,
}

// --- Queries ---

/** Snapshot status per country + the register/tool catalog.
 *
 *  Fetched once, not polled: the snapshot is refreshed by an operator's schedule
 *  (ADR-0016), so there is no in-page run whose progress could be watched. */
export function useRegistersOverview() {
  return useQuery({
    queryKey: registersKeys.overview(),
    queryFn: () => registersApi.overview(),
    staleTime: 5 * 60_000,
  })
}

/** Minimum query length before the snapshot search fires (the API enforces the same). */
export const SNAPSHOT_SEARCH_MIN_CHARS = 3

/** Typing pause before a search leaves the browser. Each request is 1-2 Typesense
 *  queries plus a batched follow-up, so a per-keystroke typeahead costs a
 *  multiple of that for results nobody reads. */
const SEARCH_DEBOUNCE_MS = 250

/** One page of a free-text search in one country's snapshot; idle until the
 *  query has 3+ characters, and debounced so typing does not fan out. */
export function useSnapshotSearch(
  country: RegisterCountry,
  q: string,
  kind: SearchKind = 'all',
  limit = 10,
  page = 1
) {
  const trimmed = useDebounced(q.trim(), SEARCH_DEBOUNCE_MS)
  return useQuery({
    queryKey: registersKeys.search(country, trimmed, kind, limit, page),
    queryFn: () => registersApi.search(country, trimmed, kind, limit, page),
    enabled: trimmed.length >= SNAPSHOT_SEARCH_MIN_CHARS,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

/** The subject's register neighbourhood (depth ≤ 2) for the report graph; 404 → null. */
export function useRegisterNeighbourhood(query: NeighbourhoodQuery | null) {
  return useQuery({
    queryKey: registersKeys.neighbourhood(query ?? {}),
    queryFn: async () => {
      if (!query) return null
      try {
        return await registersApi.neighbourhood(query)
      } catch (err) {
        if ((err as { status?: number }).status === 404) return null
        throw err
      }
    },
    enabled: !!query && !!(query.id || query.ico),
    staleTime: 5 * 60_000,
  })
}

// --- Mutations ---

/**
 * Write a picked register subject's neighbourhood into the case graph.
 *
 * Returns the written subgraph as a `GraphView` carrying real Neo4j element
 * ids — the caller merges it into the active investigation's object set. The
 * case graph is invalidated rather than patched: the import also folds the
 * subject's register properties onto the anchor node, so any cached copy of it
 * is stale.
 */
export function useImportRegisterNeighbourhood(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RegisterImportRequest) => registersApi.importNeighbourhood(caseId, body),
    onSuccess: () => {
      // Same pair the OSINT proposal import invalidates: new entities landed,
      // and the anchor now carries register properties it did not have.
      qc.invalidateQueries({ queryKey: ['graph'] })
      qc.invalidateQueries({ queryKey: ['case', caseId, 'graph'] })
      qc.invalidateQueries({ queryKey: ['case-entities-by-type'] })
    },
  })
}
